import { DEVICE_STATUS_TIMEOUT_MS } from "constant/device-status";
import { deviceStatusService } from "fleet/service/device-status";
import get from "lodash/get";
import isEqual from "lodash/isEqual";
import { delay, eventChannel } from "redux-saga";
import { call, race, put, select, take } from "redux-saga/effects";
import { directServiceSaga } from "redux-store/api-sagas";
import { USER_LOGIN } from "redux-store/auth";
import { setWsDeviceStatusOnline } from "redux-store/connection/actions";
import { getConnDeviceStatus } from "redux-store/connection/selectors";
import { storesReady } from "redux-store/utils";
import { socket } from "websocket";

import {
  setDeviceStatusesAction,
  updateAllDeviceStatusesAction,
} from "./actions";
import { updateDeviceStatusReducer } from "./reducers";
import { deviceStatusesSelector } from "./selectors";

const STORE_HYDRATION_MS = 200;

function deviceStatusHandler(emitter, message) {
  const action = updateAllDeviceStatusesAction(message);

  return emitter(action);
}

function deviceStatusEmitter(emitter) {
  const boundDeviceStatusHandler = deviceStatusHandler.bind(null, emitter);
  socket.on("device-status", boundDeviceStatusHandler);

  return () => {
    // Unsubscribe function
    socket.off("device-status", boundDeviceStatusHandler);
  };
}

function initWebSocketChannel() {
  return eventChannel(deviceStatusEmitter);
}

function* deviceStatusInitSaga() {
  /*
   * Device status stats from Overview page has dependency on global
   * devices/networks store, and the logic lives in calcDeviceStatsSelector.
   * That being said, devices/networks stores need to be ready before the UI
   * can show any device status stats.
   *
   * This saga keeps polling devices/networks store until they are ready, and
   * then make an api call to fetch device statuses snapshot. Finally it
   * dispatches an action to update deviceStatuses store.
   */
  const loginAction = yield take(USER_LOGIN);
  const { user } = loginAction.payload;
  const { account_id: accountId } = user;

  const config = {
    service: deviceStatusService.loadByAccount,
    serviceName: "deviceStatusService.loadByAccount",
    serviceArgs: [accountId],
  };
  const response = yield call(directServiceSaga, config);
  const deviceStatuses = get(response, "data.data", {});

  let storesLoaded = false;
  while (!storesLoaded) {
    yield call(delay, STORE_HYDRATION_MS);

    // eslint-disable-next-line no-console
    console.debug("polling devices/networks stores...");

    const state = yield select();
    storesLoaded = storesReady(state.global.devices, state.global.networks);
  }

  // eslint-disable-next-line no-console
  console.debug("devices/networks stores are ready, update device statuses...");

  yield put(updateAllDeviceStatusesAction(deviceStatuses));
}

/**
 * Subscribe to device-status publications from API.
 *
 * @yield {IterableIterator<*>}
 */
function* deviceStatusListenerSaga() {
  const channel = yield call(initWebSocketChannel);

  // eslint-disable-next-line no-constant-condition
  while (true) {
    const { action } = yield race({
      action: take(channel),
      timeout: call(delay, DEVICE_STATUS_TIMEOUT_MS),
    });

    // Only update WS connection status if it has changed.
    const wsOnline = yield select(getConnDeviceStatus);
    const newWsOnline = Boolean(action);
    if (wsOnline !== newWsOnline) {
      const wsOnlineAction = setWsDeviceStatusOnline(newWsOnline);
      yield put(wsOnlineAction);
    }

    // Only update device statuses if they have changed.
    if (action) {
      const dsState = yield select(deviceStatusesSelector);
      const newDsState = updateDeviceStatusReducer(dsState, action);

      const statusChanged = !isEqual(dsState, newDsState);
      if (statusChanged) {
        yield put(setDeviceStatusesAction(newDsState));
      }
    }
  }
}

export {
  deviceStatusEmitter,
  deviceStatusHandler,
  deviceStatusInitSaga,
  deviceStatusListenerSaga,
  initWebSocketChannel,
  STORE_HYDRATION_MS,
};
